package er.prateek.crossly.ads

import er.prateek.crossly.persistence.AdFrequencyState
import er.prateek.crossly.persistence.AdsPrefsStore
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.time.Clock

/**
 * Decides *how often*, given a config and what has already been shown. Deliberately knows
 * nothing about Remote Config, AdMob, or Compose -- it is the one piece of the ads stack
 * whose bugs are user-visible as "this app is unplayable", so it is kept pure enough to test
 * exhaustively with a fake clock.
 *
 * The counting and the showing are separate calls on purpose: [recordPuzzleCompleted] and
 * [shouldShowInterstitial] never spend the budget, only [recordInterstitialShown] does. An
 * interstitial that was eligible but had no filled ad to display therefore leaves the player
 * exactly as it found them, instead of silently burning their next N puzzles of quiet.
 */
class AdFrequencyController(
    private val store: AdsPrefsStore,
    private val clock: Clock = Clock.System,
    /**
     * Daily caps roll over in UTC so they behave identically for every player and can't be
     * farmed by changing the device timezone -- the same reasoning as the daily puzzle.
     */
    private val timeZone: TimeZone = TimeZone.UTC,
) {
    /** Reset by process death, unlike the persisted daily counters -- that is the point. */
    private var interstitialsThisSession = 0

    /** Increments the pacing counters. Call once per solved puzzle, before asking. */
    fun recordPuzzleCompleted() {
        val state = currentState()
        store.save(
            state.copy(
                puzzlesCompletedLifetime = state.puzzlesCompletedLifetime + 1,
                puzzlesSinceLastInterstitial = state.puzzlesSinceLastInterstitial + 1,
            ),
        )
    }

    /**
     * Every gate must pass. Order is irrelevant to the result but chosen cheapest-first, and
     * each one exists because it fails independently of the others: a player can be under
     * the daily cap yet two puzzles into a fresh install, or past the counter yet twenty
     * seconds out from the last break.
     */
    fun shouldShowInterstitial(config: InterstitialConfig): Boolean {
        if (!config.enabled) return false
        val state = currentState()

        if (state.puzzlesCompletedLifetime < config.minPuzzlesBeforeFirst) return false
        if (config.everyNPuzzles > 0 && state.puzzlesSinceLastInterstitial < config.everyNPuzzles) return false
        if (config.maxPerSession > 0 && interstitialsThisSession >= config.maxPerSession) return false
        if (config.maxPerDay > 0 && state.interstitialsToday >= config.maxPerDay) return false

        if (config.minSecondsBetween > 0 && state.lastInterstitialEpochMs > 0L) {
            val elapsedMs = nowMs() - state.lastInterstitialEpochMs
            // A negative elapsed time means the device clock moved backwards. Treat that as
            // "too soon" rather than "eligible" -- the conservative reading is the one that
            // can't be exploited by setting the clock forward and back.
            if (elapsedMs < config.minSecondsBetween * 1000L) return false
        }
        return true
    }

    /** Call only after an interstitial was actually presented to the user. */
    fun recordInterstitialShown() {
        val state = currentState()
        interstitialsThisSession++
        store.save(
            state.copy(
                puzzlesSinceLastInterstitial = 0,
                lastInterstitialEpochMs = nowMs(),
                interstitialsToday = state.interstitialsToday + 1,
            ),
        )
    }

    fun canShowRewarded(config: RewardedConfig): Boolean {
        if (!config.enabled) return false
        if (config.maxPerDay <= 0) return true
        return currentState().rewardedToday < config.maxPerDay
    }

    fun recordRewardedShown() {
        val state = currentState()
        store.save(state.copy(rewardedToday = state.rewardedToday + 1))
    }

    /**
     * Loads the persisted state with the daily counters zeroed if they belong to a previous
     * day. Done lazily on read rather than on a timer so there is no background work and no
     * dependence on the app being open at midnight.
     */
    private fun currentState(): AdFrequencyState {
        val stored = store.load()
        val today = todayKey()
        if (stored.dayKey == today) return stored
        val rolled = stored.copy(dayKey = today, interstitialsToday = 0, rewardedToday = 0)
        store.save(rolled)
        return rolled
    }

    private fun todayKey(): String = clock.todayIn(timeZone).toString()

    private fun nowMs(): Long = clock.now().toEpochMilliseconds()
}
